// useQbo — fetch hook for qboProxy actions with an in-memory session cache.
//
// Cache key includes uid + connectionId (GPT review F5: a reconnect to a
// different company must never serve the old company's numbers) + action +
// params. 5-minute TTL; `refresh()` busts one key; `clearQboCache()` empties
// everything (called on unlock change / re-lock / disconnect). In-memory
// ONLY — QB data never touches persistent storage on the client.
import { useCallback, useEffect, useRef, useState } from "react";
import { callQbo, isLockRequired } from "../../../lib/qboClient.js";

const TTL_MS = 5 * 60_000;
const cache = new Map(); // key -> { at, data }

export const clearQboCache = () => cache.clear();

const keyFor = (uid, connectionId, action, params) =>
  `${uid}|${connectionId}|${action}|${JSON.stringify(params ?? {})}`;

/**
 * ctx: { uid, connectionId, unlockToken, onLockRequired, onConnectionGone }
 * Skips fetching until ctx is complete (gate open + connected).
 */
export function useQbo(ctx, action, params = {}) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const paramsKey = JSON.stringify(params ?? {});
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  // Request generation: a slow response from a PREVIOUS action/params/
  // connection must never overwrite the current request's state — stale data
  // from the old company or filter would render as current (codex F7). The
  // cache write still happens (it lands under the OLD key, which is correct).
  const genRef = useRef(0);

  const run = useCallback(async (bust = false) => {
    const { uid, connectionId, unlockToken, onLockRequired, onConnectionGone } = ctxRef.current;
    if (!uid || !connectionId || !unlockToken) return;
    const gen = ++genRef.current;
    const key = keyFor(uid, connectionId, action, JSON.parse(paramsKey));
    if (!bust) {
      const hit = cache.get(key);
      if (hit && Date.now() - hit.at < TTL_MS) {
        setState({ data: hit.data, loading: false, error: null });
        return;
      }
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await callQbo(action, JSON.parse(paramsKey), unlockToken);
      cache.set(key, { at: Date.now(), data });
      if (gen !== genRef.current) return; // superseded — drop silently
      setState({ data, loading: false, error: null });
    } catch (e) {
      if (gen !== genRef.current) return; // superseded — drop silently
      if (isLockRequired(e)) {
        onLockRequired?.();
        return;
      }
      if (e.code === "functions/failed-precondition") {
        // Not connected / connection expired — flip the app-level state.
        onConnectionGone?.(e.message);
      }
      setState({ data: null, loading: false, error: e });
    }
  }, [action, paramsKey]);

  useEffect(() => {
    run(false);
  }, [run, ctx.uid, ctx.connectionId, ctx.unlockToken]);

  const refresh = useCallback(() => run(true), [run]);
  return { ...state, refresh };
}
